import { useContext, useRef, useState } from 'react'
import { useClickAway } from 'react-use'
import { ChevronDownThinIcon } from 'src/components/Icons/ChevronDownThinIcon'
import { GreenCheck } from 'src/components/Icons/GreenCheck'
import { ProductContext } from 'src/components/restructure/product/contexts'
import { formatterPrice } from 'src/utils/formatterPrice'

import type { ProductPack } from '../AlbumSkuSelector'

const LIMIT_QUANTITY = 30

interface QuantitySelectorProps {
  productsPack: ProductPack[]
}

export function QuantitySelector({ productsPack }: QuantitySelectorProps) {
  const { currentSku, quantity, setQuantity } = useContext(ProductContext)
  const ref = useRef(null)
  const [isOpen, setIsOpen] = useState(false)

  const { sellerOffer } = currentSku.sellerDefault
  const availableQuantity = currentSku.sellerDefault.sellerOffer.quantity
  const maxQuantity = Math.min(availableQuantity, LIMIT_QUANTITY)
  const { listPrice, cashPrice } = sellerOffer
  const currentPackInfo = productsPack.find(
    (pack) => pack.skuId === currentSku.itemId
  )

  const hasDiscount = listPrice > cashPrice

  useClickAway(ref, () => isOpen && setIsOpen(false))

  return (
    <div className="w-full mt-4 bg-neutral02 py-4 px-6">
      <p className="mb-4 flex items-center gap-2 font-semibold text-xl text-restructure-primary">
        {formatterPrice(cashPrice)}{' '}
        {hasDiscount && (
          <span className="text-tertiary font-medium text-sm line-through">
            {formatterPrice(listPrice)}
          </span>
        )}
      </p>
      {currentPackInfo && (
        <>
          <p className="mb-[10px] text-tertiary text-sm font-regular">
            Incluso neste pack:
          </p>
          <ul className="mb-[14px] flex-col md:flex-row flex flex-wrap gap-3">
            {currentPackInfo?.packItems?.map((packItem) => (
              <li
                key={packItem}
                className="flex gap-2 items-center text-restructure-primary text-sm"
              >
                <GreenCheck /> {packItem}
              </li>
            ))}
          </ul>
        </>
      )}

      <div
        className="flex flex-col bg-white border border-neutral01 pl-4 pr-2 relative"
        ref={ref}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex justify-between items-center w-full h-12"
        >
          <p className="text-base text-restructure-primary font-normal">
            {`${quantity} ${quantity > 1 ? 'unidades' : 'unidade'}`}
          </p>

          <div className={`rotate-0 ${isOpen ? '-rotate-180' : ''}`}>
            <ChevronDownThinIcon />
          </div>
        </button>

        {isOpen && (
          <div className="pr-[6px] absolute z-[100] bottom-0 top-full left-0 w-full h-fit bg-white">
            <ul className="px-4 sm:max-h-[210px] overflow-auto flex flex-col">
              {Array.from({ length: maxQuantity }, (_, i) => i + 1).map(
                (value) => (
                  <li key={`quantity-select-${value}`} className="last:pb-2">
                    <button
                      className="flex w-full items-center text-sm text-restructure-primary font-normal min-h-6 py-3 border-t border-t-gray"
                      onClick={() => {
                        setQuantity(value)
                        setIsOpen(false)
                      }}
                    >
                      {value}
                    </button>
                  </li>
                )
              )}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}
